import { isDebugBuild, isTuningEligible, isTuningSessionLease } from '@playcastdotio/PeerCore';
import type { ITuningClock } from '@playcastdotio/PeerCore';
import constants from '@playcastdotio/GlobalConfig';
import type { PeerCoordinator } from '../peer/PeerCoordinator';
import type { GamepadManager } from '../peer/managers/GamepadManager';
import type { IIpcService } from '../../services/ipc/IIpcService';
import { HostTuningIpcService } from '../../services/streamTuning/HostTuningIpcService';
import type { HostTuningPreloadBridge } from './HostTuningContracts';
import type { StreamTuningCoordinator } from './StreamTuningCoordinator';
import { HostTrialInputBinding } from './trials/HostTrialInputBinding';
import { installHostExperiments } from './experiments/createHostExperimentSession';

let current: StreamTuningCoordinator | undefined;

/** The host panel reads the same authority used by the loopback agent API. */
export function getHostStreamTuningControl(): StreamTuningCoordinator | undefined { return current; }

export function initializeStreamTuning(options: {
  peer: PeerCoordinator; ipc: IIpcService; gamepad: GamepadManager; hostId(): string;
}): HostTrialInputBinding | undefined {
  const eligible = () => isTuningEligible(isDebugBuild(), window.location.search);
  if (!eligible() || current) return undefined;
  const bridge = (window as unknown as { playcastStreamTuning?: HostTuningPreloadBridge }).playcastStreamTuning;
  const localTransport = new HostTuningIpcService(bridge);
  const input = new HostTrialInputBinding({ ipc: options.ipc, hostId: options.hostId,
    canNeutralizeGamepad: peerId => options.gamepad.canNeutralizePeer(peerId),
    neutralizeGamepad: async peerId => options.gamepad.neutralizePeer(peerId) });
  const clock: ITuningClock = { monotonicMs: () => performance.now(), wallMs: () => Date.now() };
  const id = () => crypto.randomUUID();
  const explicitArm = new URLSearchParams(window.location.search).get('streamTuning') === '1';
  current = options.peer.configureStreamTuning({ clock, id, eligible, input, localTransport,
    buildId: `${constants.VERSION}:${constants.BUILD_NUMBER}:${constants.BRANCH}`, runtimeId: id(),
    arm: explicitArm && bridge ? async (sessionId, ttlMs) => {
      const lease = await localTransport.arm(sessionId, ttlMs);
      if (!isTuningSessionLease(lease)) throw new Error('invalid-tuning-lease');
      return lease;
    } : undefined });
  const binding = options.peer.getStreamTuningBinding();
  if (binding) installHostExperiments(binding, clock, id);
  window.addEventListener('beforeunload', () => { current = undefined; void binding?.dispose(); }, { once: true });
  return input;
}
